import React from 'react';
import ProgressBar from "./ProgressBar";
import ScoreWheel from "./ScoreWheel";
import { findMinValue, findMaxValue, calculateMetricValue } from './scorecardUtils';

/** Tag: displays a little pill for "tags" metrics. */
function Tag({ label = '', value, style = {} }) {
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '13px',
                fontWeight: 500,
                backgroundColor: style.backgroundColor || '#f3f4f6',
                color: style.color || '#6b7280',
                margin: '4px',
            }}
        >
      {label}: {value}
    </span>
    );
}

/** ComparisonValue: shows e.g. +3.2% or -4.0, with item-level colors. */
function ComparisonValue({
                             label,
                             value,
                             format = 'percentage',
                             positiveColor = '#10b981',
                             negativeColor = '#dc2626',
                         }) {
    // Check for invalid values and use 0 instead
    if (value === null || value === undefined || isNaN(value)) {
        value = 0; // Default to zero instead of showing N/A
    }

    // Display as e.g. +2.1% (if differenceType=percentage) or +2.1 (absolute)
    const isPct = format === 'percentage';
    const displayed = isPct
        ? `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
        : `${value >= 0 ? '+' : ''}${value.toFixed(1)}`;

    return (
        <div
            style={{
                textAlign: 'center',
                padding: '8px',
                borderRadius: '6px',
                background: '#f8fafc',
                fontSize: '14px',
            }}
        >
      <span
          style={{
              color: value >= 0 ? positiveColor : negativeColor,
          }}
      >
        {displayed}
      </span>
            <br />
            {label}
        </div>
    );
}

function getScoreColor(score) {
    if (score <= 65) {
        return {
            main: '#ef4444',
            gradient: ['#ef4444', '#f97316'], // red -> orange
        };
    } else if (score <= 80) {
        return {
            main: '#eab308',
            gradient: ['#f97316', '#84cc16'], // orange -> lime
        };
    } else {
        return {
            main: '#22c55e',
            gradient: ['#84cc16', '#22c55e'], // lime -> green
        };
    }
}

function evaluateCondition(conditionString, record) {
    try {
        return new Function('record', `return ${conditionString}`)(record);
    } catch {
        // If invalid, just treat it as false
        return false;
    }
}

function calculateMetricValueOld(records, metricConfig) {
    if (!records || records.length === 0 || !metricConfig) return 0;

    const { calculation = {} } = metricConfig;
    const { type, fields, condition } = calculation;

    // If there's a condition, filter
    const filtered = condition
        ? records.filter((r) => evaluateCondition(condition, r))
        : records;
    if (filtered.length === 0) return 0;

    switch (type) {
        case 'average': {
            if (!fields || fields.length === 0) return 0;
            let sum = 0;
            for (const rec of filtered) {
                const fieldVals = fields.map((f) => Number(rec[f] || 0));
                const avgVal = fieldVals.reduce((a, b) => a + b, 0) / fields.length;
                sum += avgVal;
            }
            return sum / filtered.length;
        }
        case 'count':
            return filtered.length;
        case 'percentage':
            // (# passing condition / total) * 100
            return (filtered.length / records.length) * 100;
        default:
            return 0;
    }
}

/*export function findMinValue(records, metricDef) {
    if (!records.length) return 0;

    // Calculate valid values
    const validValues = records
        .map(record => {
            const val = calculateMetricValue([record], metricDef);
            return isNaN(val) ? null : val;
        })
        .filter(val => val !== null);

    return validValues.length > 0 ? Math.min(...validValues) : 0;
}

export function findMaxValue(records, metricDef) {
    if (!records.length) return 0;

    // Calculate valid values
    const validValues = records
        .map(record => {
            const val = calculateMetricValue([record], metricDef);
            return isNaN(val) ? null : val;
        })
        .filter(val => val !== null);

    return validValues.length > 0 ? Math.max(...validValues) : 0;
}*/

function AgentScorecard({
                                           name,
                                           records,
                                           allRecords,
                                           config,
                                           rank = 999,
                                           style,
                                           metricMaxValues = {},
                                           isSelected = false,
                                           onSelect,
                                       }) {
    const showTrophies = config?.showTrophies ?? config?.general?.showTrophies;

    const metrics = {};
    (config.metrics?.wheels || []).forEach((wheel) => {
        metrics[wheel.id] = calculateMetricValue(records, wheel);
    });
    (config.metrics?.bars || []).forEach((bar) => {
        metrics[bar.id] = calculateMetricValue(records, bar);
    });
    (config.metrics?.tags || []).forEach((tag) => {
        metrics[tag.id] = calculateMetricValue(records, tag);
    });

    // Compute a highlight border style for selected state with thicker border
    const selectedStyle = isSelected ? {
        borderColor: '#10b981',
        borderWidth: '3px',
        boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.3)'
    } : {};

    return (
        <div
            style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, .1)',
                position: 'relative',
                minWidth: '280px',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                ...style,
                ...selectedStyle
            }}
            onClick={() => onSelect && onSelect(name)}
        >
            {showTrophies && rank <= 2 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '-12px',
                        right: '-12px',
                        width: '32px',
                        height: '32px',
                        background: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, .1)',
                        fontSize: '20px',
                    }}
                >
                    {rank === 0 ? '🏆' : rank === 1 ? '🥈' : '🥉'}
                </div>
            )}

            <h3
                style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#111827',
                    marginBottom: '20px',
                }}
            >
                {name}
            </h3>

            {(config.metrics?.wheels || []).map((wheel) => {
                const maxValue = wheel.useCustomMaxValue ? wheel.maxValue : metricMaxValues[wheel.id] || 100;
                return (
                    <ScoreWheel
                        key={wheel.id}
                        value={metrics[wheel.id] || 0}
                        label={wheel.label}
                        displayAsPercentage={wheel.displayAsPercentage !== false}
                        maxValue={maxValue}
                    />
                );
            })}

            {(config.metrics?.bars || []).map((bar) => {
                const maxValue = bar.useCustomMaxValue ? bar.maxValue : metricMaxValues[bar.id] || 100;
                return (
                    <ProgressBar
                        key={bar.id}
                        value={metrics[bar.id] || 0}
                        label={bar.label}
                        displayAsPercentage={bar.displayAsPercentage !== false}
                        maxValue={maxValue}
                    />
                );
            })}

            {config.metrics?.tags?.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #f3f4f6',
                    }}
                >
                    {config.metrics.tags.map((tag) => (
                        <Tag
                            key={tag.id}
                            label={tag.label}
                            value={metrics[tag.id] || 0}
                            style={tag.style}
                        />
                    ))}
                </div>
            )}

            {config.showComparisons && Array.isArray(config.comparisons) && config.comparisons.length > 0 && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${config.comparisons.length}, 1fr)`,
                        gap: '8px',
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #f3f4f6',
                    }}
                >
                    {config.comparisons.map((cmp) => {
                        const metricDef = [
                            ...(config.metrics?.wheels || []),
                            ...(config.metrics?.bars || []),
                        ].find((m) => m.id === cmp.metric);

                        if (!metricDef) {
                            return (
                                <div
                                    key={cmp.id}
                                    style={{
                                        textAlign: 'center',
                                        padding: '8px',
                                        borderRadius: '6px',
                                        background: '#fef2f2',
                                        fontSize: '14px',
                                        color: '#b91c1c',
                                    }}
                                >
                                    Missing metric: <strong>{cmp.metric}</strong>
                                </div>
                            );
                        }

                        const agentValue = calculateMetricValue(records, metricDef);

                        // Define relevant here - this was the missing part
                        let relevant = allRecords;
                        if (cmp.condition && cmp.condition.trim()) {
                            relevant = allRecords.filter((r) => evaluateCondition(cmp.condition, r));
                        }

                        let baselineVal = 0;
                        switch (cmp.baseline) {
                            case 'min':
                                baselineVal = findMinValue(relevant, metricDef);
                                break;
                            case 'max':
                                baselineVal = findMaxValue(relevant, metricDef);
                                break;
                            case 'count':
                                baselineVal = relevant.length;
                                break;
                            default:
                                baselineVal = calculateMetricValue(relevant, metricDef);
                                break;
                        }

                        // Calculate difference
                        let diff;
                        if (agentValue === null || baselineVal === null ||
                            isNaN(agentValue) || isNaN(baselineVal)) {
                            diff = 0; // Use 0 instead of NaN
                        } else if (cmp.differenceType === 'absolute') {
                            diff = agentValue - baselineVal;
                        } else {
                            // Avoid division by zero
                            diff = baselineVal !== 0 ? ((agentValue - baselineVal) / baselineVal) * 100 : 0;
                        }

                        return (
                            <ComparisonValue
                                key={cmp.id}
                                label={cmp.label}
                                value={diff}
                                format={cmp.differenceType}
                                positiveColor={cmp.style?.positiveColor || '#10b981'}
                                negativeColor={cmp.style?.negativeColor || '#dc2626'}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// At the bottom of AgentScorecard.js, replace the export with:
export default React.memo(AgentScorecard, (prevProps, nextProps) => {
    // Only re-render if these specific props change

    // Compare records by IDs for efficiency
    const recordsEqual = () => {
        if (!prevProps.records && !nextProps.records) return true;
        if (!prevProps.records || !nextProps.records) return false;
        if (prevProps.records.length !== nextProps.records.length) return false;

        const prevIds = new Set(prevProps.records.map(r => r.id));
        return nextProps.records.every(r => prevIds.has(r.id));
    };

    // Compare essential props
    const areEqual =
        prevProps.name === nextProps.name &&
        prevProps.rank === nextProps.rank &&
        prevProps.isSelected === nextProps.isSelected &&
        recordsEqual() &&
        JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config) &&
        JSON.stringify(prevProps.metricMaxValues) === JSON.stringify(nextProps.metricMaxValues);

    // Optional debugging
    if (!areEqual) {
       /* console.log(`AgentScorecard ${prevProps.name} re-render`, {
            nameChanged: prevProps.name !== nextProps.name,
            rankChanged: prevProps.rank !== nextProps.rank,
            isSelectedChanged: prevProps.isSelected !== nextProps.isSelected,
            recordsChanged: !recordsEqual(),
            configChanged: JSON.stringify(prevProps.config) !== JSON.stringify(nextProps.config),
            metricMaxValuesChanged: JSON.stringify(prevProps.metricMaxValues) !== JSON.stringify(nextProps.metricMaxValues)
        });*/
    }

    return areEqual;
});
